import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, Gift, Shield, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Reviews = () => {
  const reviews = [
    {
      id: 'circusbet',
      name: 'Circusbet',
      url: 'https://www.circusbet.fr/',
      bonus: "Jusqu'à 100% de réduction",
      description: 'Une expérience de paris unique avec des cotes compétitives et une interface intuitive.',
      icon: Gift,
      color: 'from-blue-500 to-blue-600',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      rating: 4.5,
    },
    {
      id: 'bwin',
      name: 'Bwin',
      url: 'https://bwin.fr/',
      bonus: "Jusqu'à 100€ de bonus",
      description: 'Leader européen du secteur avec une réputation solide et des milliers d\'événements sportifs.',
      icon: Shield,
      color: 'from-green-500 to-green-600',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      rating: 4.7,
    },
    {
      id: 'betsson',
      name: 'Betsson',
      url: 'https://betsson.fr',
      bonus: 'Bonus de bienvenue de 100€ + 10€ de paris gratuits',
      description: 'Plateforme moderne offrant une expérience fluide sur tous les appareils avec un programme de fidélité attractif.',
      icon: Zap,
      color: 'from-purple-500 to-purple-600',
      textColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      rating: 4.6,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Avis et tests des meilleurs sites de paris
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Analyses approfondies et tests complets des plateformes de paris en ligne en France. 
            Découvrez nos avis détaillés pour faire le bon choix.
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {reviews.map((review, index) => {
            const Icon = review.icon;
            const reviewImages = [
              "/images/ruben-leija-jY_knL-TVvA-unsplash.webp",
              "/images/paulo-henrique-macedo-dias-1v7axvhwnOU-unsplash.webp",
              "/images/lesly-juarez-isWEKdSRCrA-unsplash.webp"
            ];
            return (
              <Link
                key={review.id}
                to={`/reviews/${review.id}`}
                data-discover="true"
                itemScope
                itemType="https://schema.org/Review"
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-primary-red group"
              >
                {/* Image */}
                <div className={`h-48 relative overflow-hidden`}>
                  <img 
                    src={reviewImages[index]} 
                    alt={review.name}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${review.color} opacity-80`}></div>
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm text-white`}>
                      <Icon size={14} />
                      <span>Recommandé</span>
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary-red transition-colors" itemProp="name">
                      {review.name}
                    </h3>
                    <div className="flex items-center space-x-1" itemScope itemType="https://schema.org/Rating">
                      <Star className="text-yellow-400 fill-yellow-400" size={20} />
                      <span className="font-bold text-gray-900">
                        <span itemProp="ratingValue">{review.rating}</span>
                      </span>
                      <meta itemProp="bestRating" content="5" />
                      <meta itemProp="worstRating" content="1" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">Bonus de bienvenue</p>
                    <p className={`text-lg font-bold ${review.textColor}`} itemProp="offers">{review.bonus}</p>
                  </div>

                  <p className="text-gray-600 mb-4 leading-relaxed" itemProp="description">
                    {review.description}
                  </p>

                  <div className="inline-flex items-center text-primary-red font-semibold">
                    Lire l'avis complet
                    <ExternalLink className="ml-2" size={18} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* About Our Reviews Section */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12 mb-12 shadow-lg border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Comment nous testons les sites de paris
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Chaque avis que nous publions est le résultat d'un test approfondi et indépendant. Notre 
              équipe teste personnellement chaque plateforme, de la création du compte jusqu'aux retraits, 
              en passant par les paris, les bonus, et le service client. Nous ne nous contentons pas de 
              compiler des informations trouvées ailleurs : nous vivons l'expérience pour vous.
            </p>
            <p>
              Nous évaluons chaque site selon des critères stricts : la rapidité des retraits, la qualité 
              des cotes, la variété des sports et des marchés, l'interface utilisateur, le service client, 
              et bien sûr la sécurité. Chaque critère est noté objectivement, sans influence des opérateurs. 
              Notre indépendance est totale, et aucun opérateur ne peut acheter un avis favorable.
            </p>
            <p>
              Nous testons aussi les aspects pratiques : la facilité de création de compte, la rapidité 
              de vérification d'identité, la clarté des conditions générales, la réactivité du service 
              client. Ces détails font souvent la différence entre une bonne et une excellente expérience.
            </p>
            <p>
              Nos avis sont régulièrement mis à jour. Le secteur des paris en ligne évolue rapidement : 
              les opérateurs changent leurs offres, améliorent leurs interfaces, modifient leurs conditions. 
              Nous suivons ces évolutions et actualisons nos avis pour vous garantir des informations 
              toujours à jour et pertinentes.
            </p>
            <p>
              Enfin, nous restons transparents sur nos méthodes. Chaque avis explique clairement nos 
              critères d'évaluation, nos tests effectués, et nos conclusions. Vous savez exactement 
              pourquoi nous recommandons ou ne recommandons pas un site, et vous pouvez faire votre propre 
              jugement en toute connaissance de cause.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-red to-primary-red-dark rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-xl mb-6 opacity-90">
            Consultez nos guides pour en savoir plus sur les critères de sélection
          </p>
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center bg-white text-primary-red px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Découvrir nos guides
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Reviews;

